import { useMemo } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Badge, BadgeColor } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Separator } from '@givebutter/primitives/separator';
import { maskDecimal } from '@givebutter/util';
import { faCircleExclamation, faDownload, faQrcode } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchLetterTracking } from 'api/engage';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import * as DropdownMenu from 'components/dropdown-menu';
import { ClearFiltersButton } from 'components/filter/ClearFiltersButton';
import { FilterGroupProvider } from 'components/filter/filters-next/FilterGroupContext';
import { FilterProvider } from 'components/filter/filters-next/FilterProvider';
import { QuickFiltersBar } from 'components/filter/filters-next/QuickFiltersBar';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { ServerKey } from 'components/filter/types';
import * as Input from 'components/input';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import * as Tooltip from 'components/tooltip';
import { TruncatedText } from 'components/truncated-text';
import { useAppState } from 'customHooks/useAppState';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { inlineAddress } from 'helpers/address';
import { isoDateFromServer } from 'helpers/dates';
import { capitalize, first, isEmpty, toLower } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Address, LetterFulfillment, LetterFulfillmentMailStatus } from 'types/domain';

const columnHelper = createColumnHelper<LetterFulfillment>();
const PAGE_SIZES = [5, 10, 20];

const LetterEasySendTracking = () => {
    const currencySymbol = useCurrencySymbol();
    const { apiUrl } = useAppState();
    const { accountId } = useCurrentAccount();
    const { data: letter } = useCurrentLetter();
    const { pagination, setPagination } = Table.useReactTableState({
        pageSize: PAGE_SIZES[0],
    });
    const { filters } = useFiltersByKey('mailingsTracking');
    const match = useRouteMatch();
    const [search, setSearch] = usePersistentSearch();
    const debouncedSearch = useDebounce(search);
    const [isPending, params] = useIsPending(
        {
            page: pagination.pageIndex + 1,
            per_page: pagination.pageSize,
            filters,
            query: debouncedSearch,
            include: 'transactions',
        },
        500,
    );

    const { data: tracking } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageMessageTrackingWithFilters(accountId, letter.id, params),
        queryFn: () => fetchLetterTracking(accountId, letter.id, params),
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('to_address_snapshot.name', {
                header: 'Name',
                cell: info => {
                    const contactId = info.row.original.contact_id;
                    const hasContact = info.row.original.has_non_trashed_contact;

                    if (hasContact) {
                        return (
                            <Link
                                to={`/accounts/${accountId}/contacts/${contactId}`}
                                className={'font-medium'}
                            >
                                {info.getValue()}
                            </Link>
                        );
                    }

                    return <div className={'text-gray-500'}>{info.getValue()}</div>;
                },
            }),
            columnHelper.accessor('to_address_snapshot', {
                header: 'Address',
                cell: info => {
                    const isError = info.row.original.error_type === 'undeliverable-address';
                    const address = info.row.original.to_address_snapshot;
                    const inlinedAddress = inlineAddress({
                        address_1: address.address_line1,
                        address_2: address.address_line2,
                        city: address.address_city,
                        state: address.address_state,
                        zipcode: address.address_zip,
                        country: address.address_country,
                    } as Address);
                    return (
                        <div className={'flex items-center gap-1.5'}>
                            <TruncatedText>
                                <p>{inlinedAddress}</p>
                            </TruncatedText>
                            {isError && (
                                <Tooltip.Root>
                                    <Tooltip.Trigger>
                                        <FontAwesomeIcon
                                            className={'text-yellow-500'}
                                            icon={faCircleExclamation}
                                        />
                                    </Tooltip.Trigger>
                                    <Tooltip.Portal>
                                        <Tooltip.Content inverse>
                                            <p>
                                                We were unable to deliver your letter to this
                                                address. You can update the address and retry to
                                                send this letter using a table action.
                                            </p>
                                            <Tooltip.Arrow inverse />
                                        </Tooltip.Content>
                                    </Tooltip.Portal>
                                </Tooltip.Root>
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('provider_mail_status', {
                header: 'Status',
                cell: info => {
                    const status = info.getValue();
                    const internalStatus = info.row.original.status;
                    const providerStatus = info.row.original.provider_status;

                    if (status) {
                        return <StatusBadgeWithTooltip status={status} />;
                    }

                    if (internalStatus === 'failed' || providerStatus === 'failed') {
                        return <Badge color="error">Failed</Badge>;
                    }

                    if (internalStatus) {
                        return <Badge>{capitalize(internalStatus)}</Badge>;
                    }

                    return <p className={'text-gray-400'}>Status not available</p>;
                },
            }),
            ...(letter.has_trackable_qr_codes
                ? [
                      columnHelper.accessor('is_scanned', {
                          header: 'QR Code',
                          cell: info => {
                              if (info.getValue()) {
                                  const lastScanned = info.row.original.last_scanned_at;
                                  return (
                                      <Tooltip.Root>
                                          <Tooltip.Trigger asChild>
                                              <Badge color="primary">
                                                  <FontAwesomeIcon icon={faQrcode} />
                                                  <p>Scanned</p>
                                              </Badge>
                                          </Tooltip.Trigger>
                                          <Tooltip.Portal>
                                              <Tooltip.Content inverse>
                                                  <p>
                                                      {lastScanned &&
                                                          isoDateFromServer(lastScanned).toFormat(
                                                              'LLL d, hh:mm:ss a ZZZZ',
                                                          )}
                                                  </p>
                                                  <Tooltip.Arrow inverse />
                                              </Tooltip.Content>
                                          </Tooltip.Portal>
                                      </Tooltip.Root>
                                  );
                              }

                              return <p className="font-medium text-gray-600">Not scanned</p>;
                          },
                      }),
                      columnHelper.accessor('revenue', {
                          header: 'Revenue',
                          cell: info => {
                              const revenue = maskDecimal(info.getValue());
                              return `${currencySymbol}${revenue}`;
                          },
                      }),
                  ]
                : []),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const mailPieceId = info.row.original.id;
                    const trackingNumber = info.row.original.tracking_number;
                    const contactId = info.row.original.to_address_snapshot.contact_id;
                    const hasContact = info.row.original.has_non_trashed_contact;
                    const transactions = info.row.original.transactions ?? [];
                    const onlyTransaction = first(transactions);
                    const isPDFAvailable = info.row.original.is_pdf_download_available;
                    const isAddressError = info.row.original.error_type === 'undeliverable-address';

                    return (
                        <DropdownMenu.Root>
                            <Table.RowDropdownTrigger />
                            <DropdownMenu.Content sideOffset={5} align={'end'}>
                                {trackingNumber && (
                                    <DropdownMenu.AnchorItem
                                        target={'_blank'}
                                        href={getUspsTrackingLink(trackingNumber)}
                                        rel="noreferrer"
                                    >
                                        Track on USPS
                                    </DropdownMenu.AnchorItem>
                                )}
                                <DropdownMenu.LinkItem
                                    to={`/accounts/${accountId}/contacts/${contactId}`}
                                    disabled={!hasContact}
                                >
                                    View contact
                                </DropdownMenu.LinkItem>
                                <DropdownMenu.LinkItem
                                    disabled={transactions.length === 0}
                                    onSelect={e => e.preventDefault()}
                                    to={
                                        transactions.length === 1
                                            ? `/accounts/${accountId}/transactions/${onlyTransaction?.id}`
                                            : `${match.url}/mailings/${mailPieceId}/transactions`
                                    }
                                >
                                    View transaction
                                </DropdownMenu.LinkItem>
                                {isPDFAvailable && (
                                    <DropdownMenu.AnchorItem
                                        target={'_blank'}
                                        download={true}
                                        href={`${apiUrl}/dashboard/accounts/${accountId}/messages/${letter.id}/mail-pieces/${mailPieceId}/download-pdf`}
                                        rel="noreferrer"
                                    >
                                        Download PDF <FontAwesomeIcon icon={faDownload} />
                                    </DropdownMenu.AnchorItem>
                                )}
                                {isAddressError && (
                                    <DropdownMenu.LinkItem
                                        onSelect={e => e.preventDefault()}
                                        to={`${match.url}/mailings/${mailPieceId}/retry`}
                                    >
                                        Retry
                                    </DropdownMenu.LinkItem>
                                )}
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ],
        [accountId, currencySymbol, letter.id, apiUrl, match.url, letter.has_trackable_qr_codes],
    );

    const table = useReactTable({
        data: tracking.data,
        columns,
        pageCount: tracking.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
    });

    useSyncPaginatedData(table, tracking);

    const quickFilters = ['status'].concat(
        letter.has_trackable_qr_codes ? ['amount', 'is_scanned'] : [],
    ) as ServerKey[];

    return (
        <FilterProvider cacheKey={'mailingsTracking'} resource={'mailingsTracking'}>
            <Box>
                <div className="px-6 py-4 text-xl font-medium">Recipients</div>
                <Separator />
                <div className={'flex flex-wrap justify-between gap-4 px-4 py-3'}>
                    <div className={'flex flex-wrap items-center gap-2'}>
                        <FilterGroupProvider showInnerOperator={false} showOuterOperator={false}>
                            <QuickFiltersBar quickFilters={quickFilters} />
                            <ClearFiltersButton />
                        </FilterGroupProvider>
                    </div>
                    <Input.Root size={'sm'}>
                        <Input.SearchIcon />
                        <Input.Form
                            placeholder={'Search'}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </Input.Root>
                </div>
                <Separator />
                <Table.Root
                    isLoading={isPending}
                    table={table}
                    searchTerm={debouncedSearch}
                    isFiltered={!isEmpty(filters)}
                    variant="bordered"
                >
                    <Table.Content>
                        <Table.Header />
                        <Table.Body />
                    </Table.Content>
                    <Table.Empty variant={'secondary'}>
                        <Table.EmptyIcon />
                        <Table.EmptyTitle>No tracking found</Table.EmptyTitle>
                    </Table.Empty>
                    <Table.Pagination className={'border-t border-t-gray-100 p-2'}>
                        <Table.PaginationPageNavigation />
                        <Table.PaginationPerPageDropdown pageSizes={PAGE_SIZES} />
                    </Table.Pagination>
                </Table.Root>
            </Box>
        </FilterProvider>
    );
};

const getUspsTrackingLink = (trackingNumber: string) =>
    `https://tools.usps.com/go/TrackConfirmAction_input?strOrigTrackNum=${trackingNumber}`;

const getStatusTooltipContent = (status: LetterFulfillmentMailStatus) => {
    switch (status) {
        case 'Mailed':
            return 'Mailing has been handed off to USPS for shipping';
        case 'In Transit':
            return 'Mailing is being processed by USPS origin facility';
        case 'In Local Area':
            return 'Mailing is being process at the USPS destination facility';
        case 'Processed for Delivery':
            return 'Mailing is scheduled for delivery at the recipients nearest USPS';
        case 'Delivered':
            return 'Mailing has been delivered to the recipients address';
        case 'Re-Routed':
            return 'Mailing has been re-routed due to a recipient change of address, address errors, or USPS relabeling';
        case 'Returned to Sender':
            return 'Mailing is being returned to sender due to address errors';
        case 'Received':
            return 'Order has been received and being sent to the printers';
        case 'In Production':
            return 'Mailing is in production, being printed out and stuffed into envelopes';
        case 'Failed':
            return 'Mailing was unable to be processed or delivered';
        case 'International Exit':
        default:
            return status;
    }
};

const getBadgeColor = (status: LetterFulfillmentMailStatus): BadgeColor => {
    switch (status) {
        case 'Delivered':
            return 'success';
        case 'Re-Routed':
        case 'Returned to Sender':
        case 'Failed':
            return 'error';
        default:
            return 'neutral';
    }
};

interface StatusBadgeWithTooltipProps {
    status: LetterFulfillmentMailStatus;
}

const StatusBadgeWithTooltip = (props: StatusBadgeWithTooltipProps) => {
    const { status } = props;
    const tooltipContent = getStatusTooltipContent(status);
    const badgeColor = getBadgeColor(status);

    return (
        <Tooltip.Root>
            <Tooltip.Trigger>
                <Badge color={badgeColor}>{capitalize(toLower(status))}</Badge>
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content inverse>
                    <Tooltip.Arrow inverse />
                    <p>{tooltipContent}</p>
                </Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip.Root>
    );
};

export { LetterEasySendTracking };
